import { useRef } from 'preact/hooks';
import { useUser } from '../../hooks/UNSAFE_useUser';
import { useKbPan } from '../../PRIVATE_TimeComponent/hooks/useKbPan';
import { getResolvedTaskObj, type ViewportLayout } from '../utils/dataLayoutUtils';
import { getPanIntoViewValues } from '../utils/navigationUtils';
import { pan } from '../../PRIVATE_TimeComponent/utils/panUtils';
import type { GanttRowData, GanttTaskData } from '../gantt.types';

type UseGanttKbPan<
  K1,
  K2,
  D1 extends GanttRowData<K1, D1, D2>,
  D2 extends GanttTaskData<K2>
> = Parameters<typeof useKbPan>[0] & {
  viewportLayout: ViewportLayout<K1, K2, D1, D2>;
  dataBodyWidth: number;
  dataBodyHeight: number;
  viewportStartTime: number;
  viewportEndTime: number;
  scrollPosition: number;
};

/**
 * Hook that handles keyboard panning related gestures on a Gantt.
 */
const useGanttKbPan = <K1, K2, D1 extends GanttRowData<K1, D1, D2>, D2 extends GanttTaskData<K2>>({
  viewportLayout,
  dataBodyWidth,
  dataBodyHeight,
  viewportStartTime,
  viewportEndTime,
  scrollPosition,
  ...panOptions
}: UseGanttKbPan<K1, K2, D1, D2>) => {
  const { direction } = useUser();
  const isRTL = direction === 'rtl';
  const kbPanProps = useKbPan(panOptions);

  const prevRangeX = useRef({ min: 0, max: 0 });
  const prevRangeY = useRef({ min: 0, max: 0 });
  const panBy = (dx = 0, dy = 0) => {
    if (dx !== 0) {
      const { newRange: newRangeX } = pan(
        dx / dataBodyWidth,
        panOptions.getRangeX(),
        prevRangeX.current,
        panOptions.globalRangeX,
        panOptions.onRangeXChange
      );
      prevRangeX.current = newRangeX;
    }

    if (dy !== 0) {
      const { newRange: newRangeY } = pan(
        dy / dataBodyHeight,
        panOptions.getRangeY(),
        prevRangeY.current,
        panOptions.globalRangeY,
        panOptions.onRangeYChange
      );
      prevRangeY.current = newRangeY;
    }
  };

  const onKbPanTaskIntoView = (e: CustomEvent<{ id: K2 }>) => {
    let taskObj = viewportLayout.idTaskObjMap.get(e.detail.id);
    if (!taskObj) {
      // task may be outside viewport
      const unresolvedTaskObj = viewportLayout.allIdTaskObjMap.get(e.detail.id);
      if (!unresolvedTaskObj) return;
      taskObj = getResolvedTaskObj(
        unresolvedTaskObj,
        viewportStartTime,
        viewportEndTime,
        dataBodyWidth,
        scrollPosition,
        isRTL
      );
    }

    const { x, y, width: w, height: h } = taskObj;

    const region = { x, y, w, h };
    const viewportRegion = { x: 0, y: 0, w: dataBodyWidth, h: dataBodyHeight };
    const overShoot = viewportLayout.rowPadding;
    const { dx, dy } = getPanIntoViewValues(
      region,
      viewportRegion,
      isRTL,
      'auto',
      'auto',
      overShoot
    );
    panBy(dx, dy);
  };

  const onKbPanRowIntoView = (e: CustomEvent<{ id: K1 }>) => {
    const rowObj = viewportLayout.allIdRowObjMap.get(e.detail.id);
    if (!rowObj) return;

    const region = { x: 0, y: rowObj.y - scrollPosition, w: dataBodyWidth, h: rowObj.height };
    const viewportRegion = { x: 0, y: 0, w: dataBodyWidth, h: dataBodyHeight };
    const overShoot = viewportLayout.rowPadding;
    const { dy } = getPanIntoViewValues(region, viewportRegion, isRTL, 'auto', 'auto', overShoot);
    panBy(0, dy);
  };

  return panOptions.isDisabled
    ? {}
    : {
        ...kbPanProps,
        onKbPanTaskIntoView,
        onKbPanRowIntoView
      };
};

export { useGanttKbPan };
